import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateSupportQueryInput,
  SupportContact,
  SupportQuery,
  SupportQueryPage,
  TutorialList,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import {
  askSupportQuery,
  closeSupportQuery,
  getSupportContact,
  getSupportQueries,
  getTutorials,
  replyToSupportQuery,
  type SupportQueryListParams,
} from './support-api.js';

/**
 * The help screen's data layer.
 *
 * The catalogue and the contact card are effectively static for the life of a session, so both
 * are cached hard: re-fetching a tutorial list on every window focus spends a request on
 * something that changes when we deploy, not when the shop does anything.
 *
 * The threads are not. Somebody who asked a question this morning opens this screen precisely
 * to see whether it has been answered, so that query re-fetches normally.
 */

export const supportKeys = {
  all: ['support'] as const,
  tutorials: () => ['support', 'tutorials'] as const,
  contact: () => ['support', 'contact'] as const,
  queries: (params: SupportQueryListParams) => ['support', 'queries', params] as const,
};

const STATIC_FOR = 30 * 60 * 1000;

export function useTutorials(): UseQueryResult<TutorialList> {
  const client = useApiClient();
  return useQuery({
    queryKey: supportKeys.tutorials(),
    queryFn: () => getTutorials(client),
    staleTime: STATIC_FOR,
    refetchOnWindowFocus: false,
  });
}

export function useSupportContact(): UseQueryResult<SupportContact> {
  const client = useApiClient();
  return useQuery({
    queryKey: supportKeys.contact(),
    queryFn: () => getSupportContact(client),
    staleTime: STATIC_FOR,
    refetchOnWindowFocus: false,
  });
}

export function useSupportQueries(
  params: SupportQueryListParams = {},
): UseQueryResult<SupportQueryPage> {
  const client = useApiClient();
  return useQuery({
    queryKey: supportKeys.queries(params),
    queryFn: () => getSupportQueries(client, params),
  });
}

export function useAskSupport(): UseMutationResult<SupportQuery, unknown, CreateSupportQueryInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSupportQueryInput) => askSupportQuery(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: supportKeys.all });
    },
  });
}

export function useReplyToSupport(
  queryId: string,
): UseMutationResult<SupportQuery, unknown, string> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (message: string) => replyToSupportQuery(client, queryId, message),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: supportKeys.all });
    },
  });
}

export function useCloseSupportQuery(): UseMutationResult<SupportQuery, unknown, string> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (queryId: string) => closeSupportQuery(client, queryId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: supportKeys.all });
    },
  });
}
